import { collectAssetFiles } from "../asset/asset_file";
import { loadJSONManifest } from "../asset/manifest";
import {
  CommentGenerationStrategy,
  DateGenerationStrategy,
  InteractionGenerationFile,
  InteractionGenerationStrategy,
  SequenceType,
} from "../generation/generation.models";
import { SidebarStimuli } from "../generation/sidebar.generator";
import { StimuliBank } from "../generation/stimuli.bank";
import {
  BasicImagePost,
  BuzzfeedQuizPost,
  PaginatedArticlePost,
  SponsoredImagePost,
  YoutubeVideoPost,
} from "../posts/post.models";
import { ShuffledCyclingBank } from "../utils/collections/shuffled_cycling_bank";
import { combineUrl } from "../utils/url_utils";
import { AdvertisementAsset, AssetFile } from "./models/asset.models";
import { Author } from "./models/author.models";
import { ExperimentBranding } from "./models/branding.models";
import { CommentBank } from "./models/comment.models";
import { ExperimentCondition } from "./models/condition.models";
import { ExperimentManifest, SiteWideUser } from "./models/manifest.models";

const defaultAuthorBank = "./defaults/author_bank.json";
const defaultCommentBank = "./defaults/comment_bank.json";
const defaultAvatarBank = "./defaults/avatar_bank.json";
const defaultExperimentBranding = "./defaults/experiment_branding.json";
const defaultCommentStrategy = "./defaults/comment_generation.json";
const defaultDateStrategy = "./defaults/date_generation.json";
const defaultLikeStrategy = "./defaults/like_generation.json";
const defaultDislikeStrategy = "./defaults/dislike_generation.json";
const defaultShareStrategy = "./defaults/share_generation.json";

export class ManifestParser {
  async parse(
    manifest: ExperimentManifest,
    conditionName: string,
  ): Promise<StimuliBank> {
    const condition = this.loadConditionData(manifest, conditionName);

    if (condition == null) {
      throw new Error("Experiment parameters invalid!");
    }

    const defaultDateStrategy = await this.loadDateStrategy(manifest);

    return {
      name: manifest.name,
      instructions: manifest.instructions,
      contact: manifest.contact,
      mobileSupported: manifest.supports_mobile ?? false,
      generationStrategy: manifest.feed_generation,
      condition: condition,
      goal_feedback: manifest.goal_feedback,
      articles: this.loadArticleData(manifest, condition),
      advertisements: this.loadAdvertisementData(manifest, condition),
      infographics: this.loadInfographics(manifest, condition),
      neutralInfographics: this.loadNeutralInfographics(manifest, condition),
      memes: this.loadMemes(manifest, condition),
      quizzes: this.loadQuizzes(manifest, condition),
      videos: this.loadVideos(manifest, condition),
      filler: this.loadFiller(manifest, condition),
      friendPosts: this.loadFriendPosts(manifest, condition),
      sidebar: await this.loadSidebar(manifest, condition),
      dateStrategy: defaultDateStrategy,
      commentStrategy: await this.loadCommentStrategy(
        manifest,
        defaultDateStrategy,
      ),
      likeStrategy: await this.loadLikeStrategy(manifest, condition),
      dislikeStrategy: await this.loadDislikeStrategy(manifest, condition),
      shareStrategy: await this.loadShareStrategy(manifest, condition),
      authors: await this.loadAuthorBank(manifest, condition),
      comments: await this.loadCommentBank(manifest, condition),
      avatars: await this.loadAvatarBank(manifest, condition),
      branding: await this.loadExperimentBranding(manifest),
      user: await this.loadUser(manifest),
    };
  }

  loadConditionData(
    manifest: ExperimentManifest,
    conditionName: string,
  ): ExperimentCondition | undefined {
    return manifest.conditions?.find(
      (condition) => condition.id === conditionName,
    );
  }

  loadArticleData(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): PaginatedArticlePost[] {
    if (manifest.articles == null) return [];

    return (manifest.articles.files ?? []).map((article) => {
      const assetPaths = article.pages.map((page) => {
        if (!manifest.articles!.condition_specific) {
          return combineUrl(manifest.articles?.asset_root, page);
        }
        return combineUrl(
          condition.asset_root,
          manifest.articles?.asset_root,
          page,
        );
      });

      return {
        id: article.id ?? "unknown-experiment-article",

        type: "paginated-article",
        pages: assetPaths,
        /// Combine the collection-wide and article specific tags.
        tags: [...(manifest.articles?.tags ?? []), ...(article.tags ?? [])],
        includesComments: this.areCommentsEnabledFor(manifest, "articles"),
      };
    });
  }

  loadAdvertisementData(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): SponsoredImagePost[] {
    if (manifest.advertisements == null) return [];
    const advertisements = manifest.advertisements;
    const files = collectAssetFiles(
      advertisements,
      condition,
    ) as AdvertisementAsset[];

    return files.map((file) => {
      return {
        ...file,
        id: file.id ?? "unknown-experiment-advertisement",
        type: "single-image-view",
        image: file.filename,
        tags: file.tags,
        includesComments: this.areCommentsEnabledFor(
          manifest,
          "advertisements",
        ),
      };
    });
  }

  loadInfographics(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): BasicImagePost[] {
    if (manifest.infographics == null) return [];
    const infographics = manifest.infographics;
    const files = collectAssetFiles(infographics, condition);

    return files.map((file) => {
      return {
        id: file.id ?? "unknown-experiment-infographic",
        type: "image-infographic",
        image: file.filename,
        tags: file.tags,
        includesComments: this.areCommentsEnabledFor(manifest, "infographics"),
      };
    });
  }

  loadNeutralInfographics(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): BasicImagePost[] {
    if (manifest.neutral_infographics == null) return [];
    const infographics = manifest.neutral_infographics;
    const files = collectAssetFiles(infographics, condition);

    return files.map((file) => {
      return {
        id: file.id ?? "unknown-experiment-infographic",
        type: "image-infographic",
        image: file.filename,
        tags: file.tags,
        includesComments: this.areCommentsEnabledFor(manifest, "infographics"),
      };
    });
  }

  loadMemes(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): BasicImagePost[] {
    if (manifest.memes == null) return [];
    const memes = manifest.memes;
    const files = collectAssetFiles(memes, condition);

    return files.map((file) => {
      return {
        id: file.id ?? "unknown-experiment-meme",
        type: "single-image-view",
        image: file.filename,
        tags: file.tags,
        includesComments: this.areCommentsEnabledFor(manifest, "memes"),
      };
    });
  }

  loadQuizzes(
    manifest: ExperimentManifest,
    _: ExperimentCondition,
  ): BuzzfeedQuizPost[] {
    if (manifest.quizzes == null) return [];

    return manifest.quizzes.map((quiz) => {
      return {
        quiz: quiz,
        title: quiz.title,
        id: quiz.id ?? "unknown-quiz-id",
        type: "buzzfeed-quiz",
        includesComments: this.areCommentsEnabledFor(manifest, "quizzes"),
      };
    });
  }

  loadVideos(
    manifest: ExperimentManifest,
    _: ExperimentCondition,
  ): YoutubeVideoPost[] {
    if (manifest.videos == null) return [];
    return manifest.videos.files.map((video) => {
      const url = video.url;
      const videoIdRegex = /^https?:\/\/(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/;
      const watchRegex =
        /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/;
      const embedRegex =
        /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/;
      const match =
        url.match(videoIdRegex) ||
        url.match(watchRegex) ||
        url.match(embedRegex);

      if (!match) {
        throw new Error(
          `FATAL: Youtube video link ${video.url} is not a recognizable format!`,
        );
      }

      const videoId = match[1];
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;

      return {
        ...video,
        url: embedUrl,
        type: "youtube-video",
        id: video.id ?? "unknown-video-id",
        includesComments: this.areCommentsEnabledFor(manifest, "videos"),
      };
    });
  }

  loadFiller(
    manifest: ExperimentManifest,
    _: ExperimentCondition,
  ): BasicImagePost[] {
    if (manifest.filler == null) return [];
    const filler = manifest.filler;
    const fillerMemeCollection = filler.memes;
    const fillerPosts: BasicImagePost[] = [];

    if (fillerMemeCollection != null) {
      const fillerMemes = collectAssetFiles(fillerMemeCollection);

      for (const meme of fillerMemes) {
        fillerPosts.push({
          id: meme.id ?? "unknown-filler-meme",
          type: "single-image-view",
          image: meme.filename,
          tags: meme.tags,
          includesComments: this.areCommentsEnabledFor(manifest, "filler"),
        } as BasicImagePost);
      }
    }

    return fillerPosts;
  }

  loadFriendPosts(
    manifest: ExperimentManifest,
    _: ExperimentCondition,
  ): BasicImagePost[] {
    if (manifest.filler == null) return [];
    const filler = manifest.filler;
    const sharedFillerCollection = filler.friend_posts;
    const fillerPosts: BasicImagePost[] = [];

    if (sharedFillerCollection != null) {
      const sharedFiller = collectAssetFiles(sharedFillerCollection);

      for (const shared of sharedFiller) {
        fillerPosts.push({
          id: shared.id ?? "unknown-shared-filler",
          type: "single-image-view",
          image: shared.filename,
          tags: shared.tags,
          includesComments: this.areCommentsEnabledFor(manifest, "filler"),
        } as BasicImagePost);
      }
    }

    return fillerPosts;
  }

  async loadSidebar(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): Promise<SidebarStimuli> {
    return {
      shortcuts: manifest.left_sidebar?.shortcuts ?? [],
      sponsored: this.loadAdvertisementData(manifest, condition),
      authors: await this.loadAuthorBank(manifest, condition),
      avatars: await this.loadAvatarBank(manifest, condition),
      showContacts: manifest.right_sidebar?.show_contacts ?? true,
      leaderboard: manifest.right_sidebar?.leaderboard,
    };
  }

  async loadDateStrategy(
    manifest: ExperimentManifest,
  ): Promise<DateGenerationStrategy> {
    return (
      manifest.feed_generation.date_generation ??
      (await loadJSONManifest(defaultDateStrategy))
    );
  }

  async loadCommentStrategy(
    manifest: ExperimentManifest,
    defaultDateStrategy: DateGenerationStrategy,
  ): Promise<CommentGenerationStrategy> {
    const commentStrategy: CommentGenerationStrategy =
      manifest.feed_generation.comment_strategy ??
      (await loadJSONManifest(defaultCommentStrategy));

    if (commentStrategy.date_generation == null) {
      commentStrategy.date_generation = defaultDateStrategy;
    }

    return commentStrategy;
  }

  async loadLikeStrategy(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): Promise<InteractionGenerationStrategy> {
    return this._loadInteractionStrategy(
      manifest.feed_generation.like_strategy,
      defaultLikeStrategy,
      condition,
    );
  }

  async loadDislikeStrategy(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): Promise<InteractionGenerationStrategy> {
    return this._loadInteractionStrategy(
      manifest.feed_generation.dislike_strategy,
      defaultDislikeStrategy,
      condition,
    );
  }

  async loadShareStrategy(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): Promise<InteractionGenerationStrategy> {
    return this._loadInteractionStrategy(
      manifest.feed_generation.share_strategy,
      defaultShareStrategy,
      condition,
    );
  }

  async _loadInteractionStrategy(
    file: InteractionGenerationFile | undefined,
    defaultFile: string,
    condition: ExperimentCondition,
  ): Promise<InteractionGenerationStrategy> {
    if (file == null) return loadJSONManifest(defaultFile);

    if (file.condition_specific == true) {
      return loadJSONManifest(file.filename, condition.asset_root);
    } else {
      return loadJSONManifest(file.filename);
    }
  }

  async loadAuthorBank(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): Promise<Author[]> {
    const avatarGenerator = new ShuffledCyclingBank<AssetFile>(
      await this.loadAvatarBank(manifest, condition),
    );

    let authors: Author[];

    if (manifest.author_bank == null) {
      authors = await loadJSONManifest(defaultAuthorBank);
    } else {
      authors = await loadJSONManifest(manifest.author_bank, "/");
    }

    authors = authors.map((author) => {
      return {
        ...author,
        id: `${author.first.toLowerCase()}-${author.last.toLowerCase()}`,
        avatar: avatarGenerator.getNextObject(),
      };
    });

    return authors;
  }

  async loadAvatarBank(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): Promise<AssetFile[]> {
    // Using the default avatar bank should not combine asset files with the current experiment condition.
    const useCondition = manifest.avatars != null ? condition : undefined;
    return collectAssetFiles(
      manifest.avatars ?? (await loadJSONManifest(defaultAvatarBank)),
      useCondition,
    );
  }

  async loadCommentBank(
    manifest: ExperimentManifest,
    condition: ExperimentCondition,
  ): Promise<CommentBank> {
    let commentBank: CommentBank = await loadJSONManifest(defaultCommentBank);

    if (manifest.comment_bank == null) return {};

    let neutralCommentBank: CommentBank = {};

    try {
      neutralCommentBank = await loadJSONManifest(manifest.comment_bank, "/");
    } catch (error) {
      console.warn(
        "could not load neutral comment bank data, only using condition specific",
      );
    }

    let conditionSpecificBank: CommentBank = {};

    try {
      conditionSpecificBank = await loadJSONManifest(
        manifest.comment_bank,
        condition.asset_root,
      );
    } catch (error) {
      console.warn(
        "could not load condition specific comment bank data, only using neutral",
      );
    }

    commentBank = {
      ...commentBank,
      /// add on condition specific comments to the generic top-level ones.
      ...neutralCommentBank,
      ...conditionSpecificBank,
    };

    return commentBank;
  }

  async loadExperimentBranding(
    manifest: ExperimentManifest,
  ): Promise<ExperimentBranding | undefined> {
    let defaultBranding: ExperimentBranding =
      await this.loadDefaultExperimentBranding();

    return {
      ...defaultBranding,
      ...manifest.branding,
    };
  }

  async loadDefaultExperimentBranding(): Promise<ExperimentBranding> {
    return await loadJSONManifest(defaultExperimentBranding);
  }

  async loadUser(manifest: ExperimentManifest): Promise<Author> {
    const provided = manifest.user as SiteWideUser;

    if (provided == null) {
      return {
        first: "",
        last: "",
        avatar: {
          filename: "",
        },
      };
    }

    const first = provided.first;
    var last: string = provided.last ?? "";
    var avatar: string = provided.avatar;
    return {
      first: first,
      last: last,
      avatar: {
        filename: avatar,
        id: `${first.toLowerCase()}-${last.toLowerCase()}`,
      },
    };
  }

  areCommentsEnabledFor(
    manifest: ExperimentManifest,
    type: SequenceType,
  ): boolean {
    const sourcesEnabled = manifest.feed_generation.comment_strategy
      ?.enabled_for ?? [
      "advertisements",
      "articles",
      "filler",
      "infographics",
      "memes",
      "quizzes",
      "videos",
    ];

    return sourcesEnabled.includes(type);
  }
}
